import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { AuthorizationError } from "@/shared/errors/auth-errors";
import {
    Coverage,
    JOIN_AT_LATEST_FULL,
    MAX_POLL_SEGMENTS,
    POLL_CHUNKS_PER_SEGMENT,
    PollResponse,
    PollSegmentResponse,
} from "@/features/session-diagnostics/api/session-diagnostics.api";
import { ReplayEvent, StyleTracker, replaySafety } from "@/features/session-diagnostics/player/ReplayerPort";

// D6: one request every ≤1.5 s for ALL visible segments — never one request per tile.
export const POLL_INTERVAL_MS = 1_500;
// A segment's scene since its last full snapshot is kept so a player that opens later
// (the detail) starts from the same stream. Past this it waits for the next snapshot.
export const MAX_BUFFERED_EVENTS = 20_000;
// Bytes as the player's own pin budget counts them (UTF-16 of the serialised events). A
// real 2.5-minute EnvíaDinero segment is ~7.1 MiB this way (dev segment 25d688b9, 3.56 M
// characters): one segment may hold twice that, and all the segments being watched three
// such segments together; past either the scene is dropped and the state says why.
export const MAX_SEGMENT_BYTES = 16 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 48 * 1024 * 1024;
// A segment whose answer cannot be applied this many polls in a row stops being asked for.
export const MAX_SEGMENT_FAILURES = 3;

// `too_large`: the scene went past its byte budget and waits for a full snapshot that fits.
// `failed`: the segment's answer kept breaking the reader; it is no longer polled.
export type StreamPhase = "joining" | "streaming" | "gap" | "too_large" | "failed";

export type SegmentStreamState = {
    afterSeq: number;
    phase: StreamPhase;
    generation: number;
    // Only ever CLEANED events: the raw payload never leaves `apply`.
    events: ReplayEvent[];
    styles: StyleTracker;
    coverage: Coverage | null;
    // The last poll delivered a full page: already-recorded history is still arriving.
    backlog: boolean;
    // Counts each time a backlog is drained: the player re-joins at the live head then.
    catchUp: number;
    // What `events` retains (see MAX_SEGMENT_BYTES).
    bytes: number;
};

// `events` is the chunk's decoded payload as it came over the wire (hostile until
// cleaned); `null` = the chunk could not be read.
export type DecodedChunk = {
    seq: number;
    kind: "full" | "incremental";
    events: unknown[] | null;
};

export type DecodedPollSegment = {
    chunks: DecodedChunk[];
    gapOpen: boolean;
    needFullSnapshot: boolean;
    coverage: Coverage;
};

const initial = (): SegmentStreamState => ({
    afterSeq: JOIN_AT_LATEST_FULL,
    phase: "joining",
    generation: 0,
    events: [],
    styles: replaySafety.emptyTracker,
    coverage: null,
    backlog: false,
    catchUp: 0,
    bytes: 0,
});

const bytesOf = (events: ReplayEvent[]): number => JSON.stringify(events).length * 2;

// A full chunk may open with increments that belong to the scene BEFORE its snapshot;
// the scene starts at the snapshot (with the Meta that precedes it, which sizes it).
const sceneFrom = (events: ReplayEvent[]): ReplayEvent[] | null => {
    const index = events.findIndex(replaySafety.isFullSnapshot);
    if (index === -1) return null;
    const previous = events[index - 1];
    const start = previous !== undefined && replaySafety.isMeta(previous) ? index - 1 : index;
    return events.slice(start);
};

// A segment the server no longer serves (deleted early, purging, expired) withdraws what
// was drawn from it, exactly like a revoked viewer does.
const WITHDRAWN: ReadonlySet<Coverage> = new Set<Coverage>(["forbidden", "expired"]);

// FR-008: a chunk is applied only when it is the NEXT sequence of the scene on screen.
// A full snapshot is the only way across a hole; until one arrives, increments are
// consumed (the cursor moves on) but never drawn. FR-013: every chunk that is drawn is
// cleaned here, in stream order, against the stylesheets of the scene it extends.
// `othersBytes` = what every OTHER watched segment retains (the shared budget).
const apply = (state: SegmentStreamState, response: DecodedPollSegment, othersBytes = 0): SegmentStreamState => {
    if (WITHDRAWN.has(response.coverage)) return { ...initial(), coverage: response.coverage };
    let { afterSeq, phase, generation, events, styles, bytes } = state;
    const budget = Math.min(MAX_SEGMENT_BYTES, MAX_TOTAL_BYTES - othersBytes);
    let broken = false;
    for (const chunk of response.chunks) {
        if (broken || chunk.seq <= afterSeq) continue;
        const contiguous = chunk.seq === afterSeq + 1;
        if (chunk.kind === "full") {
            const cleaned = chunk.events === null ? null : replaySafety.sanitizeWith(chunk.events, replaySafety.emptyTracker);
            const scene = cleaned === null ? null : sceneFrom(cleaned.events);
            if (cleaned === null || scene === null) {
                if (contiguous === false) {
                    broken = true;
                    phase = "gap";
                    continue;
                }
                afterSeq = chunk.seq;
                phase = "gap";
                continue;
            }
            afterSeq = chunk.seq;
            const sceneBytes = bytesOf(scene);
            if (sceneBytes > budget) {
                phase = "too_large";
                events = [];
                bytes = 0;
                continue;
            }
            generation += 1;
            events = scene;
            bytes = sceneBytes;
            styles = cleaned.tracker;
            phase = "streaming";
            continue;
        }
        if (contiguous === false) {
            broken = true;
            phase = "gap";
            continue;
        }
        afterSeq = chunk.seq;
        if (phase !== "streaming") continue;
        const cleaned = chunk.events === null ? null : replaySafety.sanitizeWith(chunk.events, styles);
        if (cleaned === null || events.length + cleaned.events.length > MAX_BUFFERED_EVENTS) {
            phase = "gap";
            continue;
        }
        const added = bytesOf(cleaned.events);
        if (bytes + added > budget) {
            phase = "too_large";
            events = [];
            bytes = 0;
            continue;
        }
        events = [...events, ...cleaned.events];
        bytes += added;
        styles = cleaned.tracker;
    }
    if ((response.needFullSnapshot || response.gapOpen) && phase === "streaming") phase = "gap";
    const backlog = response.chunks.length >= POLL_CHUNKS_PER_SEGMENT;
    const catchUp = state.backlog && backlog === false ? state.catchUp + 1 : state.catchUp;
    return { afterSeq, phase, generation, events, styles, coverage: response.coverage, backlog, catchUp, bytes };
};

export const segmentCursor = { initial, apply };

export type PollStoreStatus = "idle" | "ok" | "degraded" | "forbidden";

export type SegmentPollDeps = {
    poll: (segments: { segmentId: string; afterSeq: number }[]) => Promise<PollResponse>;
    decode: (payloadGzipB64: string) => Promise<unknown[] | null>;
    setTimer: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>;
    clearTimer: (handle: ReturnType<typeof setTimeout>) => void;
};

export type SegmentPollStore = {
    subscribe: (segmentId: string, listener: () => void) => () => void;
    getState: (segmentId: string) => SegmentStreamState | null;
    getStatus: () => PollStoreStatus;
    subscribeStatus: (listener: () => void) => () => void;
    tick: () => Promise<void>;
    dispose: () => void;
};

type Entry = { state: SegmentStreamState; listeners: Set<() => void>; failures: number };

const createSegmentPollStore = (deps: SegmentPollDeps): SegmentPollStore => {
    const entries = new Map<string, Entry>();
    const statusListeners = new Set<() => void>();
    let status: PollStoreStatus = "idle";
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let disposed = false;

    const setStatus = (next: PollStoreStatus): void => {
        if (next === status) return;
        status = next;
        statusListeners.forEach((listener) => listener());
    };

    const hasSubscribers = (): boolean => Array.from(entries.values()).some((entry) => entry.listeners.size > 0);

    const schedule = (ms: number): void => {
        if (disposed || timer !== null || inFlight || status === "forbidden" || hasSubscribers() === false) return;
        timer = deps.setTimer(() => {
            timer = null;
            void tick();
        }, ms);
    };

    const decodeSegment = async (segment: PollSegmentResponse): Promise<DecodedPollSegment> => ({
        chunks: await Promise.all(segment.chunks.map(async (chunk) => ({
            seq: chunk.seq,
            kind: chunk.kind,
            events: await deps.decode(chunk.payloadGzipB64),
        }))),
        gapOpen: segment.gapOpen,
        needFullSnapshot: segment.needFullSnapshot,
        coverage: segment.coverage,
    });

    const tick = async (): Promise<void> => {
        if (disposed || inFlight) return;
        // A segment nobody watches any more is dropped at the tick, not at unsubscribe:
        // a tile that hands over to the detail in the same commit keeps its stream.
        Array.from(entries.entries()).forEach(([segmentId, entry]) => {
            if (entry.listeners.size === 0) entries.delete(segmentId);
        });
        const requested = Array.from(entries.entries()).filter(([, entry]) => entry.state.phase !== "failed").slice(0, MAX_POLL_SEGMENTS);
        if (requested.length === 0) {
            setStatus("idle");
            return;
        }
        inFlight = true;
        try {
            const response = await deps.poll(requested.map(([segmentId, entry]) => ({ segmentId, afterSeq: entry.state.afterSeq })));
            for (const segment of response.segments) {
                const entry = entries.get(segment.segmentId);
                if (entry === undefined || entry.state.phase === "failed") continue;
                // One segment whose answer breaks the reader never stops the others: its
                // cursor stays for a retry, and after MAX_SEGMENT_FAILURES it is given up.
                try {
                    const decoded = await decodeSegment(segment);
                    const current = entries.get(segment.segmentId);
                    if (current === undefined) continue;
                    let othersBytes = 0;
                    entries.forEach((other, otherId) => {
                        if (otherId !== segment.segmentId) othersBytes += other.state.bytes;
                    });
                    current.state = apply(current.state, decoded, othersBytes);
                    current.failures = 0;
                } catch (error) {
                    if (error instanceof AuthorizationError) throw error;
                    entry.failures += 1;
                    if (entry.failures < MAX_SEGMENT_FAILURES) continue;
                    entry.state = { ...entry.state, phase: "failed", events: [], bytes: 0 };
                }
                entries.get(segment.segmentId)?.listeners.forEach((listener) => listener());
            }
            setStatus("ok");
        } catch (error) {
            // FR-012: a revoked or disallowed principal is cut on this very iteration, and
            // what was already on screen is withdrawn with it.
            if (error instanceof AuthorizationError) {
                entries.forEach((entry) => {
                    entry.state = { ...initial(), coverage: "forbidden" };
                    entry.listeners.forEach((listener) => listener());
                });
                setStatus("forbidden");
            } else {
                setStatus("degraded");
            }
        } finally {
            inFlight = false;
        }
        schedule(POLL_INTERVAL_MS);
    };

    const subscribe = (segmentId: string, listener: () => void): (() => void) => {
        const entry = entries.get(segmentId) ?? { state: initial(), listeners: new Set<() => void>(), failures: 0 };
        entry.listeners.add(listener);
        entries.set(segmentId, entry);
        schedule(0);
        return () => {
            entry.listeners.delete(listener);
        };
    };

    return {
        subscribe,
        getState: (segmentId) => {
            const entry = entries.get(segmentId);
            return entry === undefined ? null : entry.state;
        },
        getStatus: () => status,
        subscribeStatus: (listener) => {
            statusListeners.add(listener);
            return () => {
                statusListeners.delete(listener);
            };
        },
        tick,
        dispose: () => {
            disposed = true;
            if (timer !== null) deps.clearTimer(timer);
            timer = null;
            entries.clear();
        },
    };
};

export const segmentPollStore = { create: createSegmentPollStore };

export const SegmentPollContext = createContext<SegmentPollStore | null>(null);

const noopUnsubscribe = (): void => undefined;

export const useSegmentStream = (segmentId: string | null): SegmentStreamState | null => {
    const store = useContext(SegmentPollContext);
    const subscribe = useCallback(
        (listener: () => void) => (store === null || segmentId === null ? noopUnsubscribe : store.subscribe(segmentId, listener)),
        [store, segmentId],
    );
    const getSnapshot = useCallback(
        () => (store === null || segmentId === null ? null : store.getState(segmentId)),
        [store, segmentId],
    );
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export const usePollStatus = (): PollStoreStatus => {
    const store = useContext(SegmentPollContext);
    const subscribe = useCallback(
        (listener: () => void) => (store === null ? noopUnsubscribe : store.subscribeStatus(listener)),
        [store],
    );
    const getSnapshot = useCallback(() => (store === null ? "idle" : store.getStatus()), [store]);
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
